// Клиент к chat-backend (support-chat) admin endpoints. См. spec
// support-chat/docs/13-admin-panel-sso-requirements.md §11.
//
// Auth — chat-JWT, который admin-api выпускает через /chat/v1/sso-token
// (см. v62). Токен запрашивается лениво и кешируется в модуле (не в
// localStorage — он одноразовый для сессии вкладки чата).

import axios, { AxiosInstance } from 'axios';
import ApiService from './apiService';

const CHAT_BACKEND_URL =
  (process.env.REACT_APP_CHAT_BACKEND_URL as string | undefined) ||
  'https://support-chat-test.up.railway.app';

let chatTokenPromise: Promise<string> | null = null;
let cachedToken: string | null = null;
let cachedExpiresAt: number = 0;

interface SsoResp {
  data?: { token?: string; expires_at?: string };
}

// ChatApiError — типизированная ошибка, чтобы UI мог отличить
// «токен не получен от admin-api» от «chat-backend отвалил». Раньше
// axios оборачивал всё в generic Error('Network Error'), оператор не
// понимал куда копать.
export class ChatApiError extends Error {
  readonly stage: 'sso_token' | 'chat_backend';
  readonly status?: number;
  readonly originalMessage?: string;
  constructor(stage: 'sso_token' | 'chat_backend', message: string, status?: number) {
    super(message);
    this.name = 'ChatApiError';
    this.stage = stage;
    this.status = status;
    this.originalMessage = message;
  }
}

// fetchChatToken — единый источник chat-JWT. При параллельных вызовах
// возвращает один и тот же promise (de-dupe). Перезапрашивает за 1мин
// до expiry (margin против clock skew).
async function fetchChatToken(): Promise<string> {
  const now = Date.now();
  if (cachedToken && cachedExpiresAt - now > 60_000) {
    return cachedToken;
  }
  if (chatTokenPromise) return chatTokenPromise;
  chatTokenPromise = ApiService.post<SsoResp>('/chat/v1/sso-token', {})
    .then(resp => {
      const t = resp?.data?.token;
      if (!t) {
        throw new ChatApiError('sso_token', 'admin-api вернул пустой токен (CHAT_JWT_SECRET не сконфигурирован?)');
      }
      const exp = resp?.data?.expires_at;
      cachedToken = t;
      cachedExpiresAt = exp ? new Date(exp).getTime() : now + 23 * 60 * 60 * 1000;
      return t;
    })
    .catch((err: { response?: { status?: number; data?: { message?: string } }; message?: string }) => {
      const status = err?.response?.status;
      if (status === 503) {
        throw new ChatApiError(
          'sso_token',
          'admin-api: CHAT_JWT_SECRET не сконфигурирован (env). Чат SSO выключен.',
          503
        );
      }
      if (status === 401 || status === 403) {
        throw new ChatApiError('sso_token', 'Сессия admin-api истекла или нет прав', status);
      }
      throw new ChatApiError(
        'sso_token',
        `Не удалось получить chat-JWT от admin-api: ${err?.message || 'unknown'}`,
        status
      );
    })
    .finally(() => {
      chatTokenPromise = null;
    });
  return chatTokenPromise;
}

const instance: AxiosInstance = axios.create({
  baseURL: CHAT_BACKEND_URL,
  timeout: 15_000
});

// Interceptor: подмешивает Authorization Bearer перед каждым запросом.
// Ошибки fetchChatToken пробрасываются как ChatApiError(stage='sso_token').
instance.interceptors.request.use(async config => {
  const token = await fetchChatToken();
  config.headers = config.headers || {};
  (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  return config;
});

// Response interceptor: оборачивает chat-backend ошибки в ChatApiError
// со stage='chat_backend', чтобы UI отличал их от sso_token-ошибок.
instance.interceptors.response.use(
  resp => resp,
  (err: { response?: { status?: number; data?: { message?: string } }; message?: string; name?: string }) => {
    // Если это уже наш sso_token error — пропускаем без оборачивания.
    if (err instanceof ChatApiError) return Promise.reject(err);
    const status = err?.response?.status;
    const msg = err?.response?.data?.message || err?.message || 'неизвестная ошибка';
    if (!status) {
      // Network Error / CORS / DNS / timeout → axios не имеет response.
      // Самая частая причина в dev — CORS-блокировка preflight'а:
      // chat-backend Railway ALLOWED_ORIGINS должен включать наш origin.
      return Promise.reject(
        new ChatApiError(
          'chat_backend',
          `chat-backend недоступен: ${msg}. Возможные причины: CORS (ALLOWED_ORIGINS не включает ${typeof window !== 'undefined' ? window.location.origin : 'наш origin'}), DNS, или сервис лежит.`
        )
      );
    }
    return Promise.reject(new ChatApiError('chat_backend', `chat-backend HTTP ${status}: ${msg}`, status));
  }
);

// Сбрасывает кеш токена (на logout/смену оператора). Не вызывается из UI —
// reload страницы после logout обнуляет module-state.
export function resetChatApiCache() {
  cachedToken = null;
  cachedExpiresAt = 0;
  chatTokenPromise = null;
}

// ───── Endpoints (см. doc 13 §11.2) ─────

export interface ChatProjectSecrets {
  widget_shared_secret: string;
  telegram_webhook_secret?: string | null;
  telegram_webhook_url?: string | null;
}

export interface ChatProjectWidgetStatus {
  installed: boolean;
  last_seen_at: string | null;
  active_sessions_24h: number;
}

// POST /api/admin/projects — создать chat-side запись (или 409 если есть).
// id передаём = UUID проекта в admin-api (см. doc 13 §11.1 — связка).
export async function chatCreateProject(id: string, name: string, slug?: string) {
  return instance
    .post('/api/admin/projects', { id, name, slug: slug || undefined })
    .then(r => r.data);
}

// GET /api/admin/projects/:id/secrets — auth: admin JWT. 404 → проекта в
// chat-backend ещё нет (надо создать).
export async function chatGetProjectSecrets(id: string): Promise<ChatProjectSecrets | null> {
  try {
    const resp = await instance.get<ChatProjectSecrets>(`/api/admin/projects/${id}/secrets`);
    return resp.data;
  } catch (e) {
    const err = e as { response?: { status?: number } };
    if (err?.response?.status === 404) return null;
    throw e;
  }
}

// GET /api/admin/projects/:id/widget-status — 404 → null (ещё не создан).
export async function chatGetWidgetStatus(id: string): Promise<ChatProjectWidgetStatus | null> {
  try {
    const resp = await instance.get<ChatProjectWidgetStatus>(`/api/admin/projects/${id}/widget-status`);
    return resp.data;
  } catch (e) {
    const err = e as { response?: { status?: number } };
    if (err?.response?.status === 404) return null;
    throw e;
  }
}

// POST /api/admin/projects/:id/regenerate-widget-secret?confirm=true —
// destructive: старые embed snippet'ы сломаются.
export async function chatRegenerateWidgetSecret(id: string): Promise<ChatProjectSecrets> {
  const resp = await instance.post<ChatProjectSecrets>(
    `/api/admin/projects/${id}/regenerate-widget-secret`,
    null,
    { params: { confirm: 'true' } }
  );
  return resp.data;
}
